import { useTranslation } from "react-i18next";
import { Button, Icon } from "@nexus/ui";
import {
  eventTimeRangeHours,
  eventTimeRangeLabel,
  formatEventWindowShort,
  widerEventTimeRange,
  type EventTimeRange,
} from "../constants/event-time-ranges";

export function EventsEmptyState({
  timeRange,
  onTimeRangeChange,
  hasActiveFilters,
  onClearFilters,
}: {
  timeRange: EventTimeRange;
  onTimeRangeChange: (value: EventTimeRange) => void;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
}) {
  const { t } = useTranslation();
  // Every list is windowed now, so "nothing here" is as often the window's doing
  // as the filters'. Naming the window and offering the next one out is what
  // stops an empty table from looking like an empty database.
  const wider = widerEventTimeRange(timeRange);

  return (
    <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
      <p className="text-lg font-semibold leading-snug">
        {t("admin.events.empty.title", "No results match your filters")}
      </p>
      <p className="text-xs text-muted-foreground">
        {t("admin.events.empty.description", "Nothing published in the last {{window}} matched.", {
          window: formatEventWindowShort(eventTimeRangeHours(timeRange), t),
        })}
      </p>
      <div className="mt-1 flex flex-wrap items-center justify-center gap-2">
        {wider ? (
          <Button
            variant="outline"
            size="sm"
            className="h-8 rounded-sm text-xs"
            onClick={() => onTimeRangeChange(wider)}
          >
            <Icon name="clock" size="xs" aria-hidden />
            {t("admin.events.empty.widen", "Try {{range}}", {
              range: eventTimeRangeLabel(wider, t),
            })}
          </Button>
        ) : null}
        {hasActiveFilters ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 rounded-sm text-xs"
            onClick={onClearFilters}
          >
            {t("admin.events.empty.clear", "Clear filters")}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
